import styled from 'styled-components'
import * as d3 from 'd3'
import ChartNote from 'components/ChartNote'
import { AnimatedNote } from './AnimatedNote'
import useGetElementWidth from 'Hooks/useGetElementWidth'
import { ChartContext } from './UseChartContext'
import { useRef } from 'react'
import { animated } from '@react-spring/web'
import { useSpring } from '@react-spring/core'
import { IoSunnySharp } from 'react-icons/io5'
import { useTranslation, Trans } from 'react-i18next'
import '../../i18n'

function formatSwissMonth(date: Date, language: string) {
  const monthAbbr = date.toLocaleDateString(`${language}-CH`, {
    month: 'short',
  });

  const month = date.toLocaleDateString(`${language}-CH`, {
    month: 'long',
  });
  
  // German exceptions
  if (language === 'de') {
    // In German, Mai and März don't get dots when abbreviated
    if (monthAbbr === 'Mai') {
      return monthAbbr;
    } else if (month === 'Juni' || month === 'Juli' || monthAbbr === 'Mär') {
      return month;
    }
  }
  
  // French exceptions (add if needed)
  if (language === 'fr') {
    if (monthAbbr === 'mars' || monthAbbr === 'mai' || monthAbbr === 'juin' || monthAbbr === 'août') {
      return monthAbbr;
    }
  }
  
  // Add a dot if one isn't already present
  return monthAbbr.endsWith('.') ? monthAbbr : monthAbbr + '.';
}

const DaylightChart = ({ data, step }: { data: MyCsvRow[]; step: number }) => {
  const { t, i18n } = useTranslation()
  const areaRef = useRef<SVGPathElement>(null)
  const isMobile = window.innerWidth < 768
  const summerTime = [new Date('2024-03-31'), new Date('2024-10-27')]
  const [elementRef, width] = useGetElementWidth()
  const height = isMobile ? window.innerHeight - 120 : width * 0.5
  const xDomain = [
    d3.min(data, (d) => d.date) ?? new Date(),
    d3.max(data, (d) => d.date) ?? new Date(),
  ]
  const yDomain = [1, 24]
  const margin = {
    top: 50,
    right: isMobile ? 0 : 20,
    bottom: 20,
    left: isMobile ? 70 : 100,
  }

  const xScale = d3
    .scaleTime()
    .domain(xDomain)
    .range([margin.left, width - margin.right])

  const yScale = d3
    .scaleLinear()
    .domain(yDomain.reverse())
    .range([height - margin.bottom, margin.top])

  const lineGenerator = d3.line()

  const areaGenerator = d3
    .area<MyCsvRow>()
    .x((d) => xScale(d.date))
    .y0((d) => yScale(d.sunriseDecimal))
    .y1((d) => yScale(d.sunsetDecimal))

  const sunriseData: [number, number][] = data.map((d) => {
    const time =
      d.date >= summerTime[0] && d.date < summerTime[1]
        ? d.sunriseDecimal - 1
        : d.sunriseDecimal
    return [xScale(d.date), yScale(time)]
  })

  const sunsetData: [number, number][] = data.map((d) => {
    const time =
      d.date >= summerTime[0] && d.date < summerTime[1]
        ? d.sunsetDecimal - 1
        : d.sunsetDecimal

    return [xScale(d.date), yScale(time)]
  })

  const areaData = [...data].map((d) => {
    const sunsetDecimalVal =
      d.date >= summerTime[0] && d.date < summerTime[1]
        ? d.sunsetDecimal - 1
        : d.sunsetDecimal

    const sunriseDecimalVal =
      d.date >= summerTime[0] && d.date < summerTime[1]
        ? d.sunriseDecimal - 1
        : d.sunriseDecimal

    return {
      ...d,
      sunsetDecimal: sunsetDecimalVal,
      sunriseDecimal: sunriseDecimalVal,
    }
  })

  const spring = useSpring({
    data: step > 3 ? 0 : 1,
    onChange: (v) => {
      const pct = v.value.data

      const m = [...data].map((d) => {
        const sunsetDecimalVal =
          d.date >= summerTime[0] && d.date < summerTime[1]
            ? d.sunsetDecimal - pct
            : d.sunsetDecimal

        const sunriseDecimalVal =
          d.date >= summerTime[0] && d.date < summerTime[1]
            ? d.sunriseDecimal - pct
            : d.sunriseDecimal

        return {
          ...d,
          sunsetDecimal: sunsetDecimalVal,
          sunriseDecimal: sunriseDecimalVal,
        }
      })

      // Directly update the path
      if (areaRef.current) {
        const lineGenerator = d3.line()
        const newPathD = areaGenerator(m)
        areaRef.current.setAttribute('d', newPathD ?? '')
      }
    },
  })

  const sunrisePath = lineGenerator(sunriseData) || undefined
  const sunsetPath = lineGenerator(sunsetData.reverse()) || undefined
  const daylightPath = areaGenerator(areaData) || undefined

  const yTicks = d3.range(1, 25, 1).reverse()

  console.log(elementRef?.current?.getBoundingClientRect())

  return (
    <Screen>
      <Container ref={elementRef}>
        <ChartContext.Provider value={{ width, height }}>
          <ChartNote
            show={[0, 1].includes(step)}
            position={[xScale(new Date('2024-06-21')), yScale(17)]}
            textPosition={[xScale(new Date('2024-06-24')), yScale(2)]}
            text={t('chart:markers:solstice')}
            hideArrow
            inverted
          />

          <AnimatedNote
            chartDimensions={[width, height]}
            show={[0, 1].includes(step)}
            arrowHeadposition={[xScale(new Date('2024-03-01')), yScale(7.3)]}
            textPosition={[xScale(new Date('2024-03-15')), yScale(8)]}
          >
            {t('chart:markers:sunrise')}
          </AnimatedNote>

          {/* <ChartNote
            show={[0, 1].includes(step)}
            position={[xScale(new Date('2024-03-01')), yScale(7.3)]}
            textPosition={[xScale(new Date('2024-03-15')), yScale(8)]}
            text={t('chart:markers:sunrise')}
          /> */}

          <AnimatedNote
            chartDimensions={[width, height]}
            show={[0, 1].includes(step)}
            arrowHeadposition={[xScale(new Date('2024-11-01')), yScale(17)]}
            textPosition={[xScale(new Date('2024-10-20')), yScale(15)]}
          >
            {t('chart:markers:sunset')}
          </AnimatedNote>

          {/* <ChartNote
            show={[0, 1].includes(step)}
            position={[xScale(new Date('2024-10-15')), yScale(12)]}
            text='Midi civil'
          /> */}

          <AnimatedNote
            chartDimensions={[width, height]}
            show={[2].includes(step)}
            arrowHeadposition={[xScale(new Date('2024-04-15')), yScale(12.9)]}
            textPosition={[xScale(new Date('2024-04-20')), yScale(14)]}
          >
            {t('chart:markers:solarMidday')}
          </AnimatedNote>

          <AnimatedNote
            chartDimensions={[width, height]}
            show={[2].includes(step)}
            arrowHeadposition={[xScale(new Date('2024-08-15')), yScale(11.9)]}
            textPosition={[xScale(new Date('2024-08-05')), yScale(10.5)]}
          >
            {t('chart:markers:civilMidday')}
          </AnimatedNote>

          <ChartNote
            show={[4, 5].includes(step)}
            position={[xScale(new Date('2024-06-15')), yScale(2)]}
            textPosition={[xScale(new Date('2024-06-20')), yScale(2)]}
            text={t('chart:markers:summerTimeNote')}
            hideArrow
            inverted
          />

          <AnimatedNote
            chartDimensions={[width, height]}
            show={[3].includes(step)}
            arrowHeadposition={[xScale(new Date('2024-07-15')), yScale(7.1)]}
            textPosition={[xScale(new Date('2024-07-25')), yScale(8)]}
          >
            {t('chart:markers:lostLight')}
          </AnimatedNote>

          <AnimatedNote
            chartDimensions={[width, height]}
            show={[3].includes(step)}
            arrowHeadposition={[xScale(new Date('2024-04-20')), yScale(21)]}
            textPosition={[xScale(new Date('2024-04-25')), yScale(22)]}
            inverted
          >
            {t('chart:markers:missingLight')}
          </AnimatedNote>
          <AnimatedNote
            chartDimensions={[width, height]}
            show={step > 3}
            arrowHeadposition={[xScale(summerTime[0]), yScale(7.5)]}
            textPosition={[xScale(new Date('2024-04-10')), yScale(9)]}
          >
            <Trans
              i18nKey='chart:markers:summerTime'
              components={{
                NoWhiteSpace: <NoWhiteSpace />,
              }}
            />
          </AnimatedNote>

          <AnimatedNote
            chartDimensions={[width, height]}
            show={step > 3}
            arrowHeadposition={[xScale(summerTime[1]), yScale(17.15)]}
            textPosition={[xScale(new Date('2024-10-12')), yScale(14.5)]}
          >
            <Trans
              i18nKey='chart:markers:winterTime'
              components={{
                NoWhiteSpace: <NoWhiteSpace />,
              }}
            />
          </AnimatedNote>

          <Svg height={height} width={width}>
            <pattern
              id='diagonalHatch'
              patternUnits='userSpaceOnUse'
              width='4'
              height='4'
              patternTransform='rotate(-45 2 2)'
            >
              <PathPattern d='M -1,2 l 6,0' />
            </pattern>

            <mask id='midiLinesMask'>
              <rect x='0' y='0' width={width} height={height} fill='white' />
            </mask>

            <defs>
              <linearGradient id='Gradient' x1='0' x2='0' y1='0' y2='1'>
                <stop stopColor='rgb(0, 0, 0)' stopOpacity={1} offset='0%' />
                <stop stopColor='rgb(0, 0, 0)' stopOpacity={0} offset='100%' />
              </linearGradient>
            </defs>

            <SunlightPathOriginal d={daylightPath} />
            <SunlightPath ref={areaRef} d={daylightPath} />
            <Line d={sunrisePath} />
            <Line d={sunsetPath} />

            {/* Lignes des heures de midi */}
            {[2, 3, 4, 5].includes(step) && (
              <MidSolLine
                style={{ stroke: '#222222' }}
                points={`${margin.left},${yScale(12)} ${
                  width - margin.right
                },${yScale(12)}`}
              />
            )}

            {[0, 1].includes(step) && (
              <VLine
                x1={xScale(new Date('2024-06-20'))}
                y1={margin.top}
                x2={xScale(new Date('2024-06-20'))}
                y2={height - margin.bottom}
              />
            )}

            {/* {step > 3 ? (
              <MidiLine
                points={`${margin.left},${yScale(12.68)} ${xScale(
                  summerTime[0]
                )},${yScale(12.68)} ${xScale(summerTime[0])},${yScale(
                  13.68
                )} ${xScale(summerTime[1])},${yScale(13.68)} ${xScale(
                  summerTime[1]
                )},${yScale(12.68)} ${width - margin.right},${yScale(12.68)}`}
              />
            ) : (
              step > 1 && (
                <MidiLine
                  points={`${margin.left},${yScale(12.68)} ${
                    width - margin.right
                  },${yScale(12.68)}`}
                />
              )
            )} */}

            {/* highlight on midi difference */}
            {[2].includes(step) && (
              <rect
                x={margin.left}
                y={yScale(12)}
                width={width - margin.right - margin.left}
                height={yScale(12.68) - yScale(12)}
                fill={'url(#diagonalHatch)'}
                opacity={0.25}
                style={{ mixBlendMode: 'difference' }}
              />
            )}

            {/* highlight on the summer time */}
            {step > 3 && (
              <rect
                x={xScale(summerTime[0])}
                y={margin.top}
                width={xScale(summerTime[1]) - xScale(summerTime[0])}
                height={height - margin.top - margin.bottom}
                fill={'#000000'}
                opacity={0.5}
                style={{ mixBlendMode: 'soft-light' }}
              />
            )}

            {/* highlight on the lost daylight */}
            {[3].includes(step) && (
              <g>
                <rect
                  x={margin.left}
                  y={yScale(4)}
                  width={width - margin.right - margin.left}
                  height={yScale(7) - yScale(4)}
                  fill={'url(#diagonalHatch)'}
                  opacity={0.5}
                  style={{ mixBlendMode: 'difference' }}
                />
                <rect
                  x={margin.left}
                  y={yScale(19)}
                  width={width - margin.right - margin.left}
                  height={yScale(21) - yScale(19)}
                  fill={'url(#diagonalHatch)'}
                  opacity={0.5}
                  style={{ mixBlendMode: 'difference' }}
                />
              </g>
            )}

            {/* Gradient to make it look better*/}
            {/* Afternoon) */}
            {/* <rect
              x={0}
              y={yScale(12)}
              width={width}
              height={height - margin.top - margin.bottom}
              fill={'url(#Gradient)'}
              opacity={0.2}
              style={{ mixBlendMode: 'soft-light' }}
            /> */}

            {/* Morning) */}
            {/* <rect
              x={0}
              y={0}
              width={width}
              height={yScale(12)}
              fill={'url(#Gradient)'}
              opacity={0.2}
              style={{
                mixBlendMode: 'soft-light',
                transform: `scaleY(1) translateY(0px)`,
              }}
            /> */}

            {/* Y grid */}
            {data.map(
              ({ date }) =>
                date.getDate() === 1 && (
                  <GridLineX
                    x1={xScale(date)}
                    x2={xScale(date)}
                    y1={margin.top}
                    y2={height - margin.bottom}
                  />
                )
            )}

            {/* X grid */}
            {yTicks.map((value) => {
              return (
                <GridLineY
                  x1={margin.left}
                  x2={width - margin.right}
                  y1={yScale(value)}
                  y2={yScale(value)}
                />
              )
            })}
          </Svg>

          {/* Y labels (hours) */}
          {yTicks.map((value) => {
            return value % 2 === 0 ? (
              <Label
                special={value === 12}
                style={{ top: yScale(value), left: isMobile ? 40 : 50 }}
              >
                {value.toString().padStart(2, '0')}
                {i18n.language === 'fr' ? ':' : '.'}00
              </Label>
            ) : null
          })}

          {/* X labels (months) */}
          {data.map(
            ({ date }) =>
              date.getDate() === 1 &&
              date.getMonth() % (isMobile ? 2 : 1) === 0 && (
                <Label style={{ left: xScale(date), top: 20 }}>
                  {formatSwissMonth(date, i18n.language)}
                </Label>
              )
          )}

          <Note style={{ top: yScale(2), left: isMobile ? 80 : 150 }}>
            <Legend>
              <SunIcon>
                <IoSunnySharp />
              </SunIcon>
              {t('chart:day')}
            </Legend>
          </Note>

          {/* <BottomNote>{t('chart:solarMiddayNote')}</BottomNote> */}
        </ChartContext.Provider>
      </Container>
    </Screen>
  )
}

const Screen = styled.div`
  height: ${({ theme }) => theme.initialHeight}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1e2021;
`

const Legend = styled.div`
  background: ${({ theme }) => `rgb(${theme.mainColor})`};
  color: #121212;
  border-radius: 0.5em;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.65);
  white-space: nowrap;
  display: flex;
  line-height: 1;
  box-shadow: 0px 0px 30px ${({ theme }) => `rgb(${theme.mainColor})`};

  font-size: 0.85em;
  padding: 0.5em 0.85em 0.5em 0.65em;

  ${({ theme }) => theme.breakpoints.down('md')} {
    font-size: 0.75em;
  }
`

const NoWhiteSpace = styled.span`
  white-space: nowrap;
`

const VLine = styled.line`
  stroke: #ffffff;
  stroke-width: 2px;
  stroke-linejoin: round;
  stroke-linecap: round;
  fill: none;
  filter: drop-shadow(2px 1px 2px #00000022);
  stroke-dasharray: 0px 5px;
  mix-blend-mode: difference;
`

const SunIcon = styled.div`
  margin-right: 0.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => `#333`};
`

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 1600px;
  margin: auto;
`

const Note = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  color: white;
`

const BottomNote = styled.div`
  font-size: 0.65em;
  line-height: 1.2;
  color: white;
  text-align: right;
  position: absolute;
  bottom: -10px;
  transform: translateY(-100%);
  width: 100%;
  padding-right: 20px;
  box-sizing: border-box;
  -webkit-font-smoothing: antialiased;

  // ${({ theme }) => theme.breakpoints.down('md')} {
  //   text-align: center;
  //   padding: 0 15px;
  // }
`

const Svg = styled.svg``

const Line = styled.path`
  stroke: #ffffffff;
  stroke-width: 1px;
  fill: none;
  mix-blend-mode: difference;
`

const Label = styled.div<{ special?: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  transform: translate(-50%, -50%);
  color: ${({ special, theme }) => (special ? `#333` : '#fff')};
  font-size: 0.75em;
  background: ${({ special, theme }) =>
    special ? `rgb(${theme.mainColor})` : 'transparent'};
  padding: 0.25em 0.5em;
  line-height: 1;
  border-radius: 6px;
`

const SunlightPath = styled(animated.path)`
  fill: ${({ theme }) => `rgb(${theme.mainColor})`};
`

const MidiLine = styled.polyline`
  stroke: #222222;
  stroke-width: 2px;
  stroke-linejoin: round;
  stroke-linecap: round;
  fill: none;
  filter: drop-shadow(2px 1px 2px #00000022);
  // mask: url(#midiLinesMask);
`

const MidSolLine = styled(MidiLine)`
  stroke-dasharray: 5px 5px;
  stroke-width: 1px;
`

const SunlightPathOriginal = styled.path`
  fill: url(#diagonalHatch);
`

const GridLineX = styled.line`
  stroke: #f5f6f733;
  stroke-width: 1px;
  stroke-dasharray: 1;
  fill: none;
  mix-blend-mode: difference;
`

const GridLineY = styled(GridLineX)`
  stroke: #f5f6f733;
  mix-blend-mode: difference;
`

const PathPattern = styled.path`
  stroke-width: 1;
  stroke: ${({ theme }) => `rgb(${theme.mainColor})`};
`

export default DaylightChart
